import {
  GridActionsCellItem,
  GridCellParams,
  GridColDef,
  GridRowId,
  GridRowModel,
} from "@mui/x-data-grid";
import { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  MeetingItemRepresentation,
  categoryColumn,
  contentColumn,
  convertToRepresentation,
  createdDateColumn,
  dueDateColumn,
  externalEmailMarkerColumn,
  getMeetingItemFromRepresentation,
  getRiskColor,
  hasRepresentationChanged,
  isStatusUpdated,
  meetingNameColumn,
  riskColumn,
  riskTranslatedLabelOptions,
  statusColumn,
  statusTranslatedLabelOptions,
} from "../models/meetingItemRepresentation";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TuneIcon from "@mui/icons-material/Tune";
import DueDateFilterDialog from "./dueDateFilterDialog";
import dayjs from "dayjs";
import { useGridApiRef } from "@mui/x-data-grid";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import { ReactComponent as MeetingAttachment } from "../icons/meeting_attachment.svg";
import { useMsal } from "@azure/msal-react";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { bindActionCreators } from "../actions/actionCreators";
import { AppContext } from "../models/applicationState";
import { Context } from "./context";
import { ItemActions } from "../actions/itemActions";
import { ListActions } from "../actions/listActions";
import dateFormatter from "../util/dateFormatter";
import { MeetingItem, MeetingItemStatus } from "../models/meetingItem";
import { Task as CoachingTask } from "../models";
import {
  ItemEditingPanel,
  addStatusUpdateSystemCommentToItem,
  hasUserComments,
  addDueDateChangeCommentToItem,
  addRiskChangeCommentToItem,
  addContentChangeCommentToItem,
  addPrioritizationChangeCommentToItem,
  addActorChangeCommentToItem,
} from "./itemEditingPanel";
import { QuickTaskEditingPanel } from "./quickTaskEditingPanel";
import {
  ItemEditingPanelSimple,
  addContentChangeCommentToItemSimple,
  addDueDateChangeCommentToItemSimple,
  addPrioritizationChangeCommentToItemSimple,
  addRiskChangeCommentToItemSimple,
  addStatusUpdateSystemCommentToItemSimple,
  addActorChangeCommentToItemSimple,
} from "./itemEditingPanelSimple";
import ItemStatus from "./itemStatus";
import { TooltipIconWithBadge } from "./tooltipIconWithBadge";
import ExternalEmailMarker from "./externalEmailMarker";
import { TooltipIcon } from "./tooltipIcon";
import { AttachmentsPane } from "./attachmentsPane";
import EditableGrid from "./editableGrid";
import { ToolbarToggles } from "./toolbarToggles";
import * as dashboardActions from "../actions/creatorDashboardActions";
import * as itemActions from "../actions/itemActions";
import * as listActions from "../actions/listActions";
import { deleteQuickTask, saveQuickTask } from "../actions/quickTaskActions";
import {
  LIBRARY_PAGE_HEADER_HEIGHT,
  LibraryPageHeader,
} from "./libraryPageHeader";
import DueDateTimeline from "./dueDateTimeLine";
import EditableDueDateCell from "./editableDueDateCell";
import { TaskService } from "../services/coachingNavigator/taskService";
import config from "../config";
import { useGridSorting } from "../util/useGridSorting";

interface CreatorDashboardPanelProps {
  showItemsForProject?: string;
  showItemsForTag?: string;
  showMyTasks?: boolean;
}

const CreatorDashboardPanel = (props: CreatorDashboardPanelProps) => {
  const CREATOR_SORT_FIELD = "creator.sortField";
  const CREATOR_SORT_DIRECTION = "creator.sortDirection";
  const HIGHLIGHTED_ITEMS_KEY = "creatorHighlightedItems";
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState<MeetingItemRepresentation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [archivedItemsCache, setArchivedItemsCache] = useState<MeetingItemRepresentation[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [showOnlyPrioritized, setShowOnlyPrioritized] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get("category"),
  );
  const [highlightedItemIds, setHghlightedItemIds] = useState<string[]>(
    JSON.parse(window.sessionStorage.getItem(HIGHLIGHTED_ITEMS_KEY)) || [],
  );
  const [timelineInterval, setTimelineInterval] = useState<number>(
    parseInt(window.localStorage.interval) || 30,
  );
  const [showAttachmentsForMeeting, setShowAttachmentsForMeeting] =
    useState(undefined);
  const [showAttachmentsForItem, setShowAttachmentsForItem] =
    useState(undefined);
  const [showAttachmentsForTask, setShowAttachmentsForTask] =
    useState(undefined);
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const appContext = useContext<AppContext>(Context);
  const [selectedProjectFilters, setSelectedProjectFilters] = useState(
    appContext.state.selectedProjectFilters,
  );
  const [showEditingPanelForItem, setShowEditingPanelForItem] =
    useState(undefined);
  const [showQuickTaskEditingPanel, setShowQuickTaskEditingPanel] =
    useState(undefined);
  const [showSimpleEditingPanel, setShowSimpleEditingPanel] =
    useState(undefined);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [dueDateFromDays, setDueDateFromDays] = useState<number>(-30);
  const [dueDateToDays, setDueDateToDays] = useState<number>(30);
  const [dueDateAnchorEl, setDueDateAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [dueDateAutoFilter, setDueDateAutoFilter] = useState<boolean>(false);
  const { instance } = useMsal();
  const [recentlyEditedRowId, setRecentlyEditedRowId] = useState<
    GridRowId | undefined
  >(undefined);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    };
  }, []);

  // Debounce search input - wait 300ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const actions = useMemo(
    () => ({
      items: bindActionCreators(
        itemActions,
        appContext.dispatch,
      ) as unknown as ItemActions,
      lists: bindActionCreators(
        listActions,
        appContext.dispatch,
      ) as unknown as ListActions,
    }),
    [appContext.dispatch],
  );
  const coachingTaskService = useMemo(
    () => new TaskService(config.api.baseUrl, "/coachingtasks"),
    [],
  );

  useEffect(() => {
    if (selectedProjectFilters !== appContext.state.selectedProjectFilters) {
      setSelectedProjectFilters(appContext.state.selectedProjectFilters);
      fetchData();
    }
  }, [
    appContext.state.selectedProjectFilters,
    selectedProjectFilters,
    navigate,
  ]);

  const startInterval = (): string => {
    const today = new Date();
    return dateFormatter.format(
      today.setDate(today.getDate() + dueDateFromDays),
    );
  };
  const endInterval = (): string => {
    const today = new Date();
    return dateFormatter.format(today.setDate(today.getDate() + dueDateToDays));
  };

  useEffect(() => {
    // update the column when presets change
    apiRef.current.updateColumns([progressColumn]);
  }, [timelineInterval, dueDateFromDays, dueDateToDays]);

  useEffect(() => {
    const existingStorage = window.sessionStorage.getItem(
      HIGHLIGHTED_ITEMS_KEY,
    );
    if (existingStorage) {
      const existingHighlightedItems = JSON.parse(existingStorage) as string[];
      if (existingHighlightedItems?.length !== highlightedItemIds?.length) {
        const stringToStore = JSON.stringify(highlightedItemIds);
        window.sessionStorage.setItem(HIGHLIGHTED_ITEMS_KEY, stringToStore);
      }
    } else {
      window.sessionStorage.setItem(
        HIGHLIGHTED_ITEMS_KEY,
        JSON.stringify(highlightedItemIds),
      );
    }
  }, [highlightedItemIds]);

  useEffect(() => {
    const searchCategory = new URLSearchParams(location.search).get("category");
    setCategoryFilter(searchCategory);
  }, [location.search]);

  const gridItems = useMemo<MeetingItemRepresentation[]>(() => {
    // Always merge archived cache when showCompleted or showArchived is on,
    // because completed tasks are stored as archived in the DB.
    const combinedItems =
      showArchived || showCompleted
        ? [...items, ...archivedItemsCache]
        : items;
    // Deduplicate by id (item could appear in both arrays during transitions)
    const seen = new Set<string>();
    const deduped = combinedItems.filter((i) => {
      if (seen.has(i.id)) return false;
      seen.add(i.id);
      return true;
    });
    let itemsToShow = deduped;
    // Filter by completion and archived status
    if (showCompleted && showArchived) {
      // Show items that are completed OR archived (union of both filters)
      itemsToShow = itemsToShow.filter(
        (i) =>
          i.status === MeetingItemStatus.Completed || i.archived === true,
      );
    } else if (showCompleted) {
      // Show ONLY completed tasks
      itemsToShow = itemsToShow.filter(
        (i) => i.status === MeetingItemStatus.Completed,
      );
    } else if (showArchived) {
      // Show ONLY archived tasks
      itemsToShow = itemsToShow.filter((i) => i.archived === true);
    } else {
      // Hide completed tasks
      itemsToShow = itemsToShow.filter(
        (i) => i.status !== MeetingItemStatus.Completed,
      );
    }
    // Filter by inactive status (OnHold/Cancelled)
    if (showInactive) {
      // Show ONLY inactive tasks
      itemsToShow = itemsToShow.filter(
        (i) =>
          i.status === MeetingItemStatus.OnHold ||
          i.status === MeetingItemStatus.Cancelled,
      );
    } else {
      // Hide inactive tasks
      itemsToShow = itemsToShow.filter(
        (i) =>
          i.status !== MeetingItemStatus.OnHold &&
          i.status !== MeetingItemStatus.Cancelled,
      );
    }
    if (showOnlyPrioritized) {
      itemsToShow = itemsToShow.filter((i) => i.prioritized);
    }
    // Apply due date filter
    if (dueDateAutoFilter) {
      // Auto filter ON: drop tasks with dueDate outside [today+fromDays, today+toDays]; null dueDates always kept
      const today = dayjs().startOf("day");
      const fromDate = today.add(dueDateFromDays, "day");
      const toDate = today.add(dueDateToDays, "day");
      itemsToShow = itemsToShow.filter((i) => {
        if (!i.dueDate) return true;
        const due = dayjs(i.dueDate).startOf("day");
        return (
          due.isAfter(fromDate.subtract(1, "day")) &&
          due.isBefore(toDate.add(1, "day"))
        );
      });
    }
    // Auto filter OFF: show ALL tasks (no filter applied)
    // Apply search filter
    if (debouncedSearchText) {
      const lowerSearchText = debouncedSearchText.toLowerCase();
      itemsToShow = itemsToShow.filter(
        (i) =>
          i.category?.toLowerCase().includes(lowerSearchText) ||
          i.content?.toLowerCase().includes(lowerSearchText) ||
          i.actor?.toLowerCase().includes(lowerSearchText) ||
          i.meetingName?.toLowerCase().includes(lowerSearchText),
      );
    }
    if (categoryFilter) {
      const target = categoryFilter.toLowerCase();
      itemsToShow = itemsToShow.filter(
        (i) => (i as any).category?.toLowerCase() === target,
      );
    }
    return itemsToShow;
  }, [
    showCompleted,
    showArchived,
    showInactive,
    showOnlyPrioritized,
    categoryFilter,
    items,
    archivedItemsCache,
    debouncedSearchText,
    dueDateFromDays,
    dueDateToDays,
    dueDateAutoFilter,
  ]);

  // Pre-sort gridItems using the custom hook — eliminates need for apiRef.applySorting()
  const { sortedRows, sortField, sortDirection, onSortModelChange } =
    useGridSorting({
      rows: gridItems,
      dateFields: ["dueDate", "createdDate"],
      localStorageFieldKey: CREATOR_SORT_FIELD,
      localStorageDirectionKey: CREATOR_SORT_DIRECTION,
    });

  async function fetchData() {
    try {
      if (!showAttachmentsForMeeting && !showAttachmentsForItem) {
        let dashboardData = undefined;
        if (props.showItemsForProject || props.showItemsForTag) {
          dashboardData = await dashboardActions.listAllWithProjectAndTag(
            props.showItemsForProject,
            props.showItemsForTag,
          );
        } else {
          dashboardData = await dashboardActions.list();
        }
        const converted = convertToRepresentation(dashboardData).filter(
          (i) =>
            !i.isSnapshot &&
            i.task &&
            (!!props.showMyTasks ||
              i.actor !== instance.getActiveAccount()?.username),
        );
        setItems(converted);
        // If archived view is active, also refresh the archived items cache
        if (showArchived) {
          let archivedData = undefined;
          if (props.showItemsForProject || props.showItemsForTag) {
            archivedData = await dashboardActions.listAllWithProjectAndTag(
              props.showItemsForProject,
              props.showItemsForTag,
              { archived: true },
            );
          } else {
            archivedData = await dashboardActions.list({ archived: true });
          }
          const archivedConverted = convertToRepresentation(archivedData).filter(
            (i) =>
              !i.isSnapshot &&
              i.task &&
              (!!props.showMyTasks ||
                i.actor !== instance.getActiveAccount()?.username),
          );
          setArchivedItemsCache(archivedConverted);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }

  const onArchived = (archived: boolean) => {
    onArchivedAsync(archived);
  };

  const onArchivedAsync = async (archived: boolean) => {
    setShowArchived(archived);
    if (!archived) {
      setArchivedItemsCache([]);
      return;
    }
    if (!showAttachmentsForMeeting && !showAttachmentsForItem) {
      let dashboardData = undefined;
      if (props.showItemsForProject || props.showItemsForTag) {
        dashboardData = await dashboardActions.listAllWithProjectAndTag(
          props.showItemsForProject,
          props.showItemsForTag,
          { archived: true },
        );
      } else {
        dashboardData = await dashboardActions.list({ archived: true });
      }
      const converted = convertToRepresentation(dashboardData).filter(
        (i) =>
          !i.isSnapshot &&
          i.task &&
          (!!props.showMyTasks ||
            i.actor !== instance.getActiveAccount()?.username),
      );
      setArchivedItemsCache(converted);
    }
  };

  const onCompleted = (completed: boolean) => {
    onCompletedAsync(completed);
  };

  const onCompletedAsync = async (completed: boolean) => {
    setShowCompleted(completed);
    // Completed tasks are archived in the DB, so we need the archived cache loaded
    if (completed && archivedItemsCache.length === 0 && !showArchived) {
      if (!showAttachmentsForMeeting && !showAttachmentsForItem) {
        let dashboardData = undefined;
        if (props.showItemsForProject || props.showItemsForTag) {
          dashboardData = await dashboardActions.listAllWithProjectAndTag(
            props.showItemsForProject,
            props.showItemsForTag,
            { archived: true },
          );
        } else {
          dashboardData = await dashboardActions.list({ archived: true });
        }
        const converted = convertToRepresentation(dashboardData).filter(
          (i) =>
            !i.isSnapshot &&
            i.task &&
            (!!props.showMyTasks ||
              i.actor !== instance.getActiveAccount()?.username),
        );
        setArchivedItemsCache(converted);
      }
    }
  };

  const onInactive = (inactive: boolean) => {
    onInactiveAsync(inactive);
  };

  const onInactiveAsync = async (inactive: boolean) => {
    setShowInactive(inactive);
  };

  const onShowOnlyPrioritized = async (showOnlyPrioritized: boolean) => {
    onShowOnlyPrioritizedAsync(showOnlyPrioritized);
  };

  const onShowOnlyPrioritizedAsync = async (showOnlyPrioritized: boolean) => {
    setShowOnlyPrioritized(showOnlyPrioritized);
  };

  const onItemDeleted = (itemRepresentation: MeetingItemRepresentation) => {
    const item = getMeetingItemFromRepresentation(itemRepresentation);
    if (item.id) {
      actions.items.remove(itemRepresentation.meetingId, item);
    }
  };

  const openItemEditingPanel = (item: MeetingItemRepresentation) => () => {
    if (!item.meetingId && item.creator === item.actor) {
      setShowQuickTaskEditingPanel({ item });
      return;
    }
    setShowSimpleEditingPanel({ item });
  };

  const onItemUpdated = async (
    itemRepresentation: MeetingItemRepresentation,
  ) => {
    if (itemRepresentation.isCoachingTask) {
      const payload = toCoachingTaskPayload(itemRepresentation);

      if (!payload) {
        console.warn("Skipping coaching task update due to incomplete payload");
        return;
      }

      setItems((prev) =>
        prev.map((row) =>
          row.id === itemRepresentation.id ? itemRepresentation : row,
        ),
      );

      try {
        await coachingTaskService.save(payload);
      } catch (err) {
        console.error("Failed to update coaching task", err);
        fetchData();
      }
      return;
    }

    const userFullName =
      appContext.state.invoker.lastName && appContext.state.invoker.firstName
        ? appContext.state.invoker?.firstName +
        " " +
        appContext.state.invoker?.lastName
        : instance.getActiveAccount()?.name;

    // Find previous item for comparison
    const previousItem = items.find((row) => row.id === itemRepresentation.id);

    if (!itemRepresentation.meetingId) {
      // Quicktask: use Simple comment functions
      // Detect status change by comparing previousItem.status to the new status
      if (previousItem && previousItem.status !== itemRepresentation.status) {
        addStatusUpdateSystemCommentToItemSimple(
          itemRepresentation,
          userFullName,
          t,
        );
      }
      if (previousItem && previousItem.dueDate !== itemRepresentation.dueDate) {
        addDueDateChangeCommentToItemSimple(
          itemRepresentation,
          userFullName,
          itemRepresentation.dueDate,
          t,
        );
      }
      if (previousItem && previousItem.risk !== itemRepresentation.risk) {
        addRiskChangeCommentToItemSimple(
          itemRepresentation,
          userFullName,
          itemRepresentation.risk,
          t,
        );
      }
      if (previousItem && previousItem.content !== itemRepresentation.content) {
        addContentChangeCommentToItemSimple(
          itemRepresentation,
          userFullName,
          t,
        );
      }
      if (
        previousItem &&
        previousItem.prioritized !== itemRepresentation.prioritized
      ) {
        addPrioritizationChangeCommentToItemSimple(
          itemRepresentation,
          userFullName,
          itemRepresentation.prioritized,
          t,
        );
      }
      if (previousItem && previousItem.actor !== itemRepresentation.actor) {
        addActorChangeCommentToItemSimple(
          itemRepresentation,
          userFullName,
          itemRepresentation.actor || "",
          t,
        );
      }
    } else {
      // Meeting task: use regular comment functions
      if (isStatusUpdated(itemRepresentation)) {
        addStatusUpdateSystemCommentToItem(itemRepresentation, userFullName, t);
      }

      if (previousItem && previousItem.dueDate !== itemRepresentation.dueDate) {
        addDueDateChangeCommentToItem(
          itemRepresentation,
          userFullName,
          itemRepresentation.dueDate,
          t,
        );
      }
      if (previousItem && previousItem.risk !== itemRepresentation.risk) {
        addRiskChangeCommentToItem(
          itemRepresentation,
          userFullName,
          itemRepresentation.risk,
          t,
        );
      }
      if (previousItem && previousItem.content !== itemRepresentation.content) {
        addContentChangeCommentToItem(itemRepresentation, userFullName, t);
      }
      if (
        previousItem &&
        previousItem.prioritized !== itemRepresentation.prioritized
      ) {
        addPrioritizationChangeCommentToItem(
          itemRepresentation,
          userFullName,
          itemRepresentation.prioritized,
          t,
        );
      }
      if (previousItem && previousItem.actor !== itemRepresentation.actor) {
        addActorChangeCommentToItem(
          itemRepresentation,
          userFullName,
          itemRepresentation.actor || "",
          t,
        );
      }
    }
    console.log("onItemUpdated:", itemRepresentation);
    // Update local state immediately
    setItems((prev) =>
      prev.map((row) =>
        row.id === itemRepresentation.id ? itemRepresentation : row,
      ),
    );
    // Persist changes for both meeting tasks and quicktasks
    const item = getMeetingItemFromRepresentation(itemRepresentation);
    if (!itemRepresentation.meetingId) {
      (item as any).meetingId = undefined;
      (item as any).comments = (itemRepresentation as any).comments;
    }
    await onItemUpserted(item);
  };

  async function executeCallbackIfDiffsExist(callback?: () => Promise<void>) {
    if (
      !showAttachmentsForMeeting &&
      !showAttachmentsForItem &&
      !props.showItemsForProject &&
      !props.showItemsForTag
    ) {
      const response = await dashboardActions.listDiffs();
      if (response.length) {
        const highlightsToStore = highlightedItemIds?.length
          ? [...highlightedItemIds]
          : [];
        const newHighlightedIds = response.map((i) => i.id);
        if (newHighlightedIds) {
          for (const highlightedItemId of newHighlightedIds) {
            if (!highlightsToStore.includes(highlightedItemId)) {
              highlightsToStore.push(highlightedItemId);
            }
          }
          setHghlightedItemIds(highlightsToStore);
        }
        if (callback) {
          callback();
        }
      }
    }
  }

  useEffect(() => {
    executeCallbackIfDiffsExist();
    // Always fetch data on the initial load
    fetchData();
    const interval = setInterval(() => {
      executeCallbackIfDiffsExist(fetchData);
    }, 10000);
    return () => clearInterval(interval);
  }, [
    navigate,
    showAttachmentsForMeeting,
    showAttachmentsForItem,
    showAttachmentsForTask,
  ]);

  const openMeetingAttachments = (meetingId: string) => () => {
    setShowAttachmentsForMeeting(meetingId);
  };

  const openItemAttachments = (row: MeetingItemRepresentation) => () => {
    if (row.sessionId) {
      setShowAttachmentsForTask({ sessionId: row.sessionId, taskId: row.id });
      return;
    }
    setShowAttachmentsForItem({ id: row.id, meetingId: row.meetingId });
  };

  const onItemUpserted = async (item: MeetingItem) => {
    if (!item.meetingId) {
      return await saveQuickTask(item);
    }
    const savedItem = await actions.items.save(item.meetingId, item);
    return savedItem;
  };

  const handleStatusChange = (
    itemId: GridRowId,
    newStatus: MeetingItemStatus,
  ) => {
    const previousRow = items.find((row) => row.id === itemId);
    if (!previousRow || previousRow.status === newStatus) return;

    const userFullName =
      appContext.state.invoker.lastName && appContext.state.invoker.firstName
        ? appContext.state.invoker?.firstName +
        " " +
        appContext.state.invoker?.lastName
        : instance.getActiveAccount()?.name;

    const updatedRow: MeetingItemRepresentation = {
      ...previousRow,
      status: newStatus,
    };
    const isQuickTask = !updatedRow.meetingId;

    if (isQuickTask) {
      addStatusUpdateSystemCommentToItemSimple(updatedRow, userFullName, t);
    } else {
      addStatusUpdateSystemCommentToItem(updatedRow, userFullName, t);
    }

    updatedRow.data = getMeetingItemFromRepresentation(updatedRow);
    if (isQuickTask) {
      (updatedRow.data as any).meetingId = undefined;
    }
    if ((updatedRow as any).comments) {
      (updatedRow.data as any).comments = (updatedRow as any).comments;
    }

    setItems((prev) =>
      prev.map((row) => (row.id === itemId ? updatedRow : row)),
    );
    onItemUpserted(updatedRow.data);

    // Set highlight for the edited row
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    setRecentlyEditedRowId(itemId);
    highlightTimeoutRef.current = setTimeout(() => {
      setRecentlyEditedRowId(undefined);
    }, 1500);
  };

  const toCoachingTaskPayload = (
    row: MeetingItemRepresentation,
  ): CoachingTask | undefined => {
    if (!row.id || !row.clientId || !row.content || !row.status) {
      return undefined;
    }

    return {
      id: row.id,
      clientId: row.clientId,
      sessionId: row.sessionId,
      sessionName: row.sessionName || row.meetingName || "",
      content: row.content,
      category: row.category || "Coaching Hub",
      actor: row.actor,
      creator: row.creator,
      status: row.status as any,
      dueDate: row.dueDate ? new Date(row.dueDate) : undefined,
    };
  };

  const processRowUpdate = (
    newRow: GridRowModel<MeetingItemRepresentation>,
  ) => {
    const previousRow = items.find((row) => row.id === newRow.id);

    if (newRow.isCoachingTask) {
      if (!previousRow || previousRow.status === newRow.status) {
        return newRow;
      }

      const mergedRow = {
        ...previousRow,
        ...newRow,
      } as MeetingItemRepresentation;
      const payload = toCoachingTaskPayload(mergedRow);

      if (!payload) {
        console.warn("Skipping coaching task update due to incomplete payload");
        return previousRow;
      }

      setItems((prev) =>
        prev.map((row) => (row.id === mergedRow.id ? mergedRow : row)),
      );
      coachingTaskService.save(payload).catch((err) => {
        console.error("Failed to update coaching task status", err);
        setItems((prev) =>
          prev.map((row) => (row.id === previousRow.id ? previousRow : row)),
        );
      });
      return mergedRow;
    }
    if (!hasRepresentationChanged(newRow)) {
      return newRow;
    }
    const userFullName =
      appContext.state.invoker.lastName && appContext.state.invoker.firstName
        ? appContext.state.invoker?.firstName +
        " " +
        appContext.state.invoker?.lastName
        : instance.getActiveAccount()?.name;

    const isQuickTask = !newRow.meetingId;

    // Status change based on previous snapshot in data
    if (isStatusUpdated(newRow)) {
      if (isQuickTask) {
        addStatusUpdateSystemCommentToItemSimple(newRow, userFullName, t);
      } else {
        addStatusUpdateSystemCommentToItem(newRow, userFullName, t);
      }
    }

    // Due date change: compare previous snapshot in data vs current
    const normalizeDate = (d: any): Date | undefined => {
      if (!d) return undefined;
      const candidate = (d as any).$d ? (d as any).$d : d;
      const parsed =
        typeof candidate === "string" || candidate instanceof String
          ? new Date(candidate as string)
          : candidate instanceof Date
            ? candidate
            : new Date(candidate);
      return isNaN(parsed.getTime()) ? undefined : parsed;
    };
    const prevDate = normalizeDate(newRow.data?.dueDate);
    const nextDate = normalizeDate(newRow.dueDate);
    const prevStr = prevDate
      ? dateFormatter.format(prevDate)
      : t("item.due_date.not_set");
    const nextStr = nextDate
      ? dateFormatter.format(nextDate)
      : t("item.due_date.not_set");
    if (prevStr !== nextStr) {
      if (isQuickTask) {
        addDueDateChangeCommentToItemSimple(newRow, userFullName, nextDate, t);
      } else {
        addDueDateChangeCommentToItem(newRow, userFullName, nextDate, t);
      }
    }

    // Actor change
    if (previousRow && previousRow.actor !== newRow.actor) {
      if (isQuickTask) {
        addActorChangeCommentToItemSimple(
          newRow,
          userFullName,
          newRow.actor || "",
          t,
        );
      } else {
        addActorChangeCommentToItem(
          newRow,
          userFullName,
          newRow.actor || "",
          t,
        );
      }
    }

    // Convert and persist
    newRow.data = getMeetingItemFromRepresentation(newRow);
    if (isQuickTask) {
      (newRow.data as any).meetingId = undefined;
    }
    if ((newRow as any).comments) {
      (newRow.data as any).comments = (newRow as any).comments;
    }
    setItems((prev) =>
      prev.map((row) => (row.id === newRow.id ? newRow : row)),
    );
    onItemUpserted(newRow.data);

    // Set highlight for the edited row
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    setRecentlyEditedRowId(newRow.id);
    highlightTimeoutRef.current = setTimeout(() => {
      setRecentlyEditedRowId(undefined);
    }, 1500);

    return newRow;
  };

  const onMeetingSelected = async (meetingId: string) => {
    actions.lists.load(meetingId);
  };

  const onNavigate = (meetingId: string) => {
    if (meetingId) {
      onMeetingSelected(meetingId);
      navigate(`/library/${meetingId}/current`);
    }
  };

  const handleClickNavigation = (item: MeetingItemRepresentation) => () => {
    onNavigate(item?.meetingId);
  };

  const isDueDateInRange = (d: string | Date | null | undefined): boolean => {
    if (!d) return false;
    const today = dayjs().startOf("day");
    const due = dayjs(d).startOf("day");
    const offset = due.diff(today, "day");
    return offset >= dueDateFromDays && offset <= dueDateToDays;
  };

  const handleDueDateDragChange = async (
    row: MeetingItemRepresentation,
    newISODate: string | null,
  ) => {
    let newDueDate: Date | undefined;
    if (newISODate) {
      const d = dayjs(newISODate).toDate();
      d.setHours(12, 0, 0, 0); // mirror dueDateColumn.valueSetter convention
      const utcTime = d.getTime() - d.getTimezoneOffset() * 60000;
      newDueDate = new Date(utcTime);
    } else {
      newDueDate = undefined;
    }
    const newRow: MeetingItemRepresentation = {
      ...row,
      dueDate: newDueDate as Date,
    };
    if (!newRow.task) newRow.task = true;
    await processRowUpdate(newRow);
  };

  const progressColumn: GridColDef<any> = {
    field: "progress",
    headerName: `${t("dashboard.progress", {
      from: startInterval(),
      to: endInterval(),
    })}`,
    align: "left",
    headerAlign: "left",
    width: 350,
    type: "actions",
    // filterable: true,
    renderCell: (params: GridCellParams) => (
      <DueDateTimeline
        dueDate={params.row.dueDate}
        previousDueDates={params.row.previousDueDates}
        fromDays={dueDateFromDays}
        toDays={dueDateToDays}
        showDot={dueDateAutoFilter || isDueDateInRange(params.row.dueDate)}
        onDueDateChange={(newDate) =>
          handleDueDateDragChange(params.row, newDate)
        }
      />
    ),
  };

  const columns: GridColDef[] = [
    {
      ...riskColumn,
      editable: true,
      type: "singleSelect",
      valueFormatter: (value: string) => t(`item_risk.${value}`),
      valueOptions: riskTranslatedLabelOptions(t),
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor: getRiskColor(params.row.risk),
            width: "auto",
            height: "32px",
            margin: "10px",
          }}
        ></Box>
      ),
    },
    {
      ...createdDateColumn,
      renderCell: (params) => (
        <Badge
          color="primary"
          badgeContent=" "
          variant="dot"
          sx={{ display: "inline" }}
          invisible={!highlightedItemIds.includes(params.row.id)}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <span>{createdDateColumn.valueFormatter(params.value)}</span>
        </Badge>
      ),
    },
    { ...meetingNameColumn, width: 150 },
    categoryColumn,
    { ...contentColumn, flex: 0.6 },
    { field: "actor", headerName: "item.actor", editable: false },
    {
      // Show yellow "E" marker after actor email for external addresses
      ...externalEmailMarkerColumn,
      renderCell: (params) => {
        const actor: string | undefined = params.row.actor;
        return (
          <ExternalEmailMarker
            email={actor}
            internalFromEmail={params.row.creator}
          />
        );
      },
    },
    {
      ...dueDateColumn,
      editable: false,
      minWidth: 145,
      maxWidth: 145,
      renderCell: (params) => (
        <EditableDueDateCell
          taskId={String(params.row.id)}
          dueDate={params.row.dueDate}
          onChange={(_taskId, newDate) =>
            handleDueDateDragChange(params.row, newDate)
          }
        />
      ),
    },
    {
      ...statusColumn,
      editable: false,
      type: "singleSelect",
      valueFormatter: (value: string) => t(`item_status.${value}`),
      valueOptions: statusTranslatedLabelOptions(t),
      renderCell: (params: GridCellParams) => (
        <ItemStatus
          status={params.row.status}
          lastModifiedBy={params.row.statusLastUpdatedBy}
          onChange={(newStatus) => handleStatusChange(params.row.id, newStatus)}
        />
      ),
    },
    progressColumn,
    // meetingAttachmentsColumn,
    // itemAttachmentsColumn
  ];

  const onArchiveToggle = (itemRepresentation: MeetingItemRepresentation) => {
    itemRepresentation.archived = itemRepresentation.archived ? false : true;
    const item = getMeetingItemFromRepresentation(itemRepresentation);
    // Ensure quicktasks don't carry a placeholder meetingId so they use quicktask route
    if (!itemRepresentation.meetingId) {
      (item as any).meetingId = undefined;
    }
    setItems((prev) => prev.filter((row) => row.id !== itemRepresentation.id));
    setArchivedItemsCache((prev) => prev.filter((row) => row.id !== itemRepresentation.id));
    onItemUpserted(item);
  };

  const toggleArchive =
    (itemRepresentation: MeetingItemRepresentation) => () => {
      onArchiveToggle(itemRepresentation);
    };

  const extraActions = (id: GridRowId, row: MeetingItemRepresentation) => {
    const isStandaloneTask = !row.meetingId;
    return [
      <GridActionsCellItem
        key="open"
        icon={
          <TooltipIconWithBadge
            titleKey={"general.open"}
            icon={<ChevronRightIcon />}
            badgeContent={hasUserComments(row, t) ? "i" : undefined}
          />
        }
        label="Edit"
        onClick={openItemEditingPanel(row)}
        style={{ color: "var(--mui-palette-primary-main)" }}
      />,
      <GridActionsCellItem
        key="meetingAttachments"
        icon={
          <TooltipIconWithBadge
            badgeContent={row.meetingAttachments}
            titleKey="dashboard.meetingAttachments"
            icon={<MeetingAttachment />}
          />
        }
        label={t("dashboard.meetingAttachments_label")}
        onClick={openMeetingAttachments(row.meetingId)}
        disabled={isStandaloneTask}
        style={{ color: "var(--mui-palette-primary-main)" }}
        showInMenu
      />,
      <GridActionsCellItem
        key="itemAttachments"
        icon={
          <TooltipIconWithBadge
            badgeContent={row.attachments}
            titleKey="dashboard.itemAttachments"
            icon={<AttachFileIcon />}
          />
        }
        label={t("dashboard.itemAttachments_label", { count: row.attachments ?? 0 })}
        onClick={openItemAttachments(row)}
        style={{ color: "var(--mui-palette-primary-main)" }}
        showInMenu
      />,
      <GridActionsCellItem
        key="openMeeting"
        icon={
          <Tooltip title={t("meeting.open")}>
            <EventNoteOutlinedIcon fontSize="medium" />
          </Tooltip>
        }
        label={t("meeting.open")}
        style={{ color: "var(--mui-palette-primary-main)" }}
        onClick={handleClickNavigation(row)}
        disabled={isStandaloneTask}
        showInMenu
      />,
      <GridActionsCellItem
        key="archive"
        icon={
          <TooltipIcon
            titleKey={row.archived ? "general.unarchive" : "general.archive"}
            icon={row.archived ? <UnarchiveIcon /> : <ArchiveOutlinedIcon />}
          />
        }
        disabled={row.creator !== instance.getActiveAccount()?.username}
        label="Archive"
        onClick={toggleArchive(row)}
        style={{ color: "var(--mui-palette-primary-main)" }}
        showInMenu
      />,
    ];
  };

  return (
    <Box
      sx={{
        pt: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CssBaseline />
      <LibraryPageHeader
        title={t("menu.creator_dashboard")}
        chipLabel={t("general.task", { count: gridItems.length })}
        rightContent={
          <>
            <ToolbarToggles
              onArchived={onArchived}
              onCompleted={onCompleted}
              onInactive={setShowInactive}
              onShowOnlyPrioritized={setShowOnlyPrioritized}
            />
            <TextField
              size="small"
              placeholder={t("search.dashboard_placeholder")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                width: 250,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                },
              }} slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ color: "text.secondary", fontSize: "1.25rem" }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: searchText && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchText("")}
                        edge="end"
                        sx={{ padding: "4px" }}
                      >
                        <ClearIcon sx={{ fontSize: "1.25rem" }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />
            <IconButton
              size="small"
              onClick={(e) => setDueDateAnchorEl(e.currentTarget)}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <TuneIcon sx={{ fontSize: "1.25rem" }} />
            </IconButton>
            {(dueDateFromDays !== -30 ||
              dueDateToDays !== 30 ||
              dueDateAutoFilter) && (
                <Tooltip title={t("dueDateFilter.clearSelectionTooltip")}>
                  <IconButton
                    size="small"
                    aria-label={t("dueDateFilter.clearSelection")}
                    onClick={() => {
                      setDueDateFromDays(-30);
                      setDueDateToDays(30);
                      setDueDateAutoFilter(false);
                    }}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <ClearIcon sx={{ fontSize: "1.25rem" }} />
                  </IconButton>
                </Tooltip>
              )}
            <DueDateFilterDialog
              anchorEl={dueDateAnchorEl}
              open={Boolean(dueDateAnchorEl)}
              onClose={() => setDueDateAnchorEl(null)}
              fromDays={dueDateFromDays}
              toDays={dueDateToDays}
              onFilterChange={(from, to) => {
                setDueDateFromDays(from);
                setDueDateToDays(to);
              }}
              autoFilter={dueDateAutoFilter}
              onAutoFilterChange={setDueDateAutoFilter}
            />
          </>
        }
      />
      {showAttachmentsForMeeting && (
        <AttachmentsPane
          allowModifications={true}
          height="70vh"
          uploadPrefix={`meetings/${showAttachmentsForMeeting}/`}
          meetingId={showAttachmentsForMeeting}
          onClose={() => setShowAttachmentsForMeeting(undefined)}
          onAttachmentsCountChanged={(count, meetingId, _itemId) =>
            setItems((prev) =>
              prev.map((item) =>
                item.meetingId === meetingId
                  ? { ...item, meetingAttachments: count }
                  : item,
              ),
            )
          }
        />
      )}
      {showAttachmentsForItem && (
        <AttachmentsPane
          allowModifications={true}
          height="70vh"
          uploadPrefix={
            showAttachmentsForItem.meetingId
              ? `items/meetings/${showAttachmentsForItem.meetingId}/items/${showAttachmentsForItem.id}/`
              : `items/quicktasks/${showAttachmentsForItem.id}/`
          }
          meetingId={showAttachmentsForItem.meetingId}
          itemId={showAttachmentsForItem.id}
          onClose={() => setShowAttachmentsForItem(undefined)}
          onAttachmentsCountChanged={(count, _meetingId, itemId) =>
            setItems((prev) =>
              prev.map((item) =>
                item.id === itemId ? { ...item, attachments: count } : item,
              ),
            )
          }
        />
      )}
      {showAttachmentsForTask && (
        <AttachmentsPane
          allowModifications={true}
          height="70vh"
          uploadPrefix={`sessions/${showAttachmentsForTask.sessionId}/tasks/${showAttachmentsForTask.taskId}/`}
          sessionId={showAttachmentsForTask.sessionId}
          taskId={showAttachmentsForTask.taskId}
          onClose={() => setShowAttachmentsForTask(undefined)}
          onAttachmentsCountChanged={(count, _sessionId, taskId) =>
            setItems(
              items.map((item) =>
                item.id === taskId ? { ...item, attachments: count } : item,
              ),
            )
          }
        />
      )}
      {showEditingPanelForItem && (
        <ItemEditingPanel
          allowAttachmentsEditing={true}
          meetingItem={showEditingPanelForItem.item}
          open={true}
          onClose={() => setShowEditingPanelForItem(undefined)}
          onUpdate={onItemUpdated}
          onAttachmentsCountChanged={(count, _meetingId, itemId) =>
            setItems((prev) =>
              prev.map((item) =>
                item.id === itemId ? { ...item, attachments: count } : item,
              ),
            )
          }
        />
      )}
      {showQuickTaskEditingPanel && (
        <QuickTaskEditingPanel
          meetingItem={showQuickTaskEditingPanel.item}
          open={true}
          allowMetadataEditing={true}
          allowAttachmentsEditing={true}
          onClose={() => setShowQuickTaskEditingPanel(undefined)}
          onUpdate={onItemUpdated}
        />
      )}
      {showSimpleEditingPanel && (
        <ItemEditingPanelSimple
          meetingItem={showSimpleEditingPanel.item}
          open={true}
          allowMetadataEditing={true}
          allowAttachmentsEditing={true}
          onClose={() => setShowSimpleEditingPanel(undefined)}
          onUpdate={onItemUpdated}
        />
      )}
      <Box sx={{ flexGrow: 1 }}>
        <EditableGrid
          height={`calc(90vh - ${LIBRARY_PAGE_HEADER_HEIGHT}px)`}
          processRowUpdate={processRowUpdate}
          rows={sortedRows}
          columns={columns}
          sortField={sortField}
          sortDirection={sortDirection}
          updateSortModel={onSortModelChange}
          sortingMode="server"
          apiRef={apiRef}
          onDelete={onItemDeleted}
          modelType={"item"}
          extraActions={extraActions}
          deleteInMenu
          toolbar={() => null}
          isCellEditable={(params) =>
            params.row.isCoachingTask ? params.field === "status" : true
          }
          highlightedRowId={recentlyEditedRowId}
          loading={isLoading}
        />
      </Box>
    </Box>
  );
};

export default CreatorDashboardPanel;
